import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../stores/hooks/hooks";
import { shallowEqual } from "react-redux";

import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMeta,
  faTiktok,
  faXTwitter,
  faYoutube,
} from "@fortawesome/free-brands-svg-icons";
import { faLink, faPhone } from "@fortawesome/pro-light-svg-icons";

import { fetchCreatorContactsById } from "../../../stores/thunks/creatorsThunk";
import { resetSelectedCreatorContacts } from "../../../stores/slice/creatorsSlice";
import { RevLink, RevSpinner } from "@revmatics/catalyst";

const ContactInfoItem = ({
  icon,
  pathname,
  text,
}: {
  icon: IconDefinition;
  pathname?: string;
  text: string;
}) => {
  return (
    <div className="flex items-start gap-2">
      <FontAwesomeIcon
        icon={icon}
        className="text-md w-5 text-primary-800/50 mt-0.5"
      />
      {pathname ? (
        <RevLink
          pathname={pathname}
          underline={false}
          text={text}
          className="break-all"
          local={false}
        />
      ) : (
        <p className="text-monochrome-900 typography-body-sm break-all">
          {text}
        </p>
      )}
    </div>
  );
};

const EmptyState = () => {
  return (
    <>
      <div className="w-full flex justify-center mb-4">
        <p className="text-monochrome-500 typography-body-sm">
          No contact information available
        </p>
      </div>
      <hr className="w-full border-t border-primary-600/20 mb-4" />
    </>
  );
};

export const ContactInfoSection = ({ creatorId }: { creatorId: number }) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(fetchCreatorContactsById(creatorId));

    return () => {
      dispatch(resetSelectedCreatorContacts());
    };
  }, [dispatch, creatorId]);

  const { contacts, isLoading } = useAppSelector(
    (state) => ({
      contacts: state.creators.selectedCreatorContacts,
      isLoading: state.creators.isLoading,
    }),
    shallowEqual,
  );

  if (isLoading) {
    return (
      <div className="w-full h-[24px] flex items-center justify-center">
        <RevSpinner />
      </div>
    );
  }

  if (!contacts) return <EmptyState />;

  const {
    phone_number,
    personal_website,
    meta_handle,
    tiktok_handle,
    x_handle,
    youtube_handle,
  } = contacts;

  const potentialContacts = [
    { value: phone_number, props: { icon: faPhone, text: phone_number } },
    {
      value: personal_website,
      props: { icon: faLink, text: "Website", pathname: personal_website },
    },
    {
      value: meta_handle,
      props: {
        icon: faMeta,
        text: meta_handle,
        pathname: `https://instagram.com/${meta_handle}`,
      },
    },
    {
      value: tiktok_handle,
      props: {
        icon: faTiktok,
        text: `@${tiktok_handle}`,
        pathname: `https://tiktok.com/@${tiktok_handle}`,
      },
    },
    {
      value: x_handle,
      props: {
        icon: faXTwitter,
        text: `@${x_handle}`,
        pathname: `https://x.com/${x_handle}`,
      },
    },
    {
      value: youtube_handle,
      props: {
        icon: faYoutube,
        text: `@${youtube_handle}`,
        pathname: `https://youtube.com/${youtube_handle}`,
      },
    },
  ];

  const creatorContacts = potentialContacts.filter((contact) => contact.value);

  if (creatorContacts.length === 0) return <EmptyState />;

  return (
    <>
      <div
        className="
                w-full gap-y-2 grid grid-cols-2 gap-x-4 mb-4
                sm:gap-y-3 sm:grid-cols-3
                xl:flex xl:flex-col 
                2xl:grid 2xl:grid-cols-2
            "
      >
        {creatorContacts.map((contact, index) => (
          <ContactInfoItem
            key={index}
            icon={contact.props.icon}
            pathname={contact.props.pathname}
            text={contact.props.text}
          />
        ))}
      </div>
      <hr className="w-full border-t border-primary-600/20 mb-4" />
    </>
  );
};
